import React, { useState } from "react";
import { Search, Package, MapPin, Clock, CheckCircle2, AlertCircle, Truck, ArrowRight } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Card, CardContent } from "./ui/card";
import { Separator } from "./ui/separator";
import { Badge } from "./ui/badge";
import { toast } from "sonner";
import { trackShipment } from "../lib/db";

const statusColors = {
  "Booked": "bg-blue-100 text-blue-800 border-blue-200",
  "In Transit": "bg-amber-100 text-amber-800 border-amber-200",
  "Out for Delivery": "bg-orange-100 text-orange-800 border-orange-200",
  "Delivered": "bg-green-100 text-green-800 border-green-200",
};

const statusIcons = {
  "Booked": Package,
  "In Transit": Truck,
  "Out for Delivery": ArrowRight,
  "Delivered": CheckCircle2,
};

const formatDateTime = (isoStr) => {
  if (!isoStr) return "";
  const d = new Date(isoStr);
  return d.toLocaleString("en-IN", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit", hour12: true,
  });
};

export const TrackingSection = ({ compact = false }) => {
  const [trackingNumber, setTrackingNumber] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const handleTrack = async () => {
    const cleaned = trackingNumber.trim();
    if (!cleaned) {
      toast.error("Please enter a docket number.");
      return;
    }
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const shipment = await trackShipment(cleaned);
      if (shipment) {
        setResult(shipment);
      } else {
        setError("No shipment found with this docket number. Please check and try again.");
      }
    } catch (err) {
      setError("Something went wrong. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleTrack();
  };

  return (
    <section className={compact ? "" : "bg-white py-10 md:py-14 border-b"}>
      <div className={compact ? "" : "max-w-4xl mx-auto px-4 sm:px-6 lg:px-8"}>
        {!compact && (
          <div className="text-center mb-8">
            <div className="flex items-center justify-center gap-2 mb-3">
              <div className="w-10 h-10 rounded-full bg-brand-lightest flex items-center justify-center">
                <Search className="w-5 h-5 text-brand" />
              </div>
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900" style={{ fontFamily: "'Lora', serif" }}>
              Track Your Shipment
            </h2>
            <p className="text-gray-500 mt-2 text-sm sm:text-base">
              Enter your Om Sai Express docket number to check the status of your shipment.
            </p>
          </div>
        )}

        <div className="bg-gray-50 rounded-xl border p-6 sm:p-8">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1">
              <Input
                type="text"
                value={trackingNumber}
                onChange={(e) => setTrackingNumber(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Enter Om Sai Express Docket Number"
                className="h-12 text-base pl-4 pr-4 border-gray-300 bg-white focus:border-brand focus:ring-brand"
              />
            </div>
            <Button
              onClick={handleTrack}
              disabled={loading}
              className="bg-brand hover:bg-brand-dark text-white font-semibold h-12 px-8 text-base shrink-0 shadow-md"
            >
              <Search className="w-5 h-5" />
              {loading ? "Tracking..." : "Track Shipment"}
            </Button>
          </div>

          {/* Error */}
          {error && (
            <div className="mt-5 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* Tracking Result */}
          {result && (
            <div className="mt-6 animate-in fade-in slide-in-from-top-2 duration-300">
              <Card className="border shadow-lg overflow-hidden">
                <div className="h-1.5 bg-brand w-full" />
                <CardContent className="p-0">
                  {/* Header */}
                  <div className="p-5 sm:p-6 bg-white">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Docket Number</p>
                        <p className="text-xl font-bold text-gray-900" style={{ fontFamily: "'Lora', serif" }}>{result.docket_number}</p>
                      </div>
                      <span className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold border ${statusColors[result.current_status] || "bg-gray-100 text-gray-700"}`}>
                        {(() => { const Icon = statusIcons[result.current_status]; return Icon ? <Icon className="w-4 h-4" /> : null; })()}
                        {result.current_status}
                      </span>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                      <div className="flex items-start gap-2">
                        <MapPin className="w-4 h-4 text-brand mt-0.5 shrink-0" />
                        <div>
                          <p className="text-xs text-gray-500">Origin</p>
                          <p className="text-sm font-medium text-gray-900">{result.origin_city}</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-2">
                        <MapPin className="w-4 h-4 text-brand mt-0.5 shrink-0" />
                        <div>
                          <p className="text-xs text-gray-500">Destination</p>
                          <p className="text-sm font-medium text-gray-900">{result.destination_city}</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-2">
                        <Clock className="w-4 h-4 text-brand mt-0.5 shrink-0" />
                        <div>
                          <p className="text-xs text-gray-500">Last Updated</p>
                          <p className="text-sm font-medium text-gray-900">{formatDateTime(result.updated_at)}</p>
                        </div>
                      </div>
                      {result.remarks && (
                        <div className="flex items-start gap-2">
                          <AlertCircle className="w-4 h-4 text-brand mt-0.5 shrink-0" />
                          <div>
                            <p className="text-xs text-gray-500">Remarks</p>
                            <p className="text-sm font-medium text-gray-900">{result.remarks}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Tracking Timeline */}
                  {result.tracking_history && result.tracking_history.length > 0 && (
                    <>
                      <Separator />
                      <div className="p-5 sm:p-6 bg-gray-50">
                        <h4 className="text-sm font-semibold text-gray-900 mb-4" style={{ fontFamily: "'Lora', serif" }}>Tracking History</h4>
                        <div className="space-y-0">
                          {[...result.tracking_history].reverse().map((entry, index) => {
                            const Icon = statusIcons[entry.status] || Package;
                            const isLatest = index === 0;
                            return (
                              <div key={index} className="flex gap-4">
                                <div className="flex flex-col items-center">
                                  <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${isLatest ? "bg-brand text-white" : "bg-gray-200 text-gray-500"}`}>
                                    <Icon className="w-4 h-4" />
                                  </div>
                                  {index < result.tracking_history.length - 1 && (
                                    <div className="w-0.5 h-8 bg-gray-200 my-1" />
                                  )}
                                </div>
                                <div className="pb-4">
                                  <p className={`text-sm font-semibold ${isLatest ? "text-gray-900" : "text-gray-600"}`}>{entry.status}</p>
                                  <p className="text-xs text-gray-500 mt-0.5">{formatDateTime(entry.date_time)}</p>
                                  {entry.location && <p className="text-xs text-gray-500">{entry.location}</p>}
                                  {entry.remarks && <p className="text-xs text-gray-600 mt-1">{entry.remarks}</p>}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          <p className="text-xs text-gray-400 mt-4 text-center">
            Track your Om Sai Express shipments using docket numbers issued at the time of booking.
          </p>
        </div>
      </div>
    </section>
  );
};
